import * as React from 'react';
import type { Maybe, WellnessPromoSettings } from '@types/gatsby-types';
import {
    addDays,
    parse,
    format,
    isWithinInterval,
    startOfWeek,
    startOfMonth,
    endOfWeek,
    endOfMonth,
    isSameWeek,
    isSameMonth,
} from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import { FALLBACK_TIMEZONE } from 'core/constants';
import useIsMobile from 'core/hooks/useIsMobile';
import type { Moment } from 'lib/types';
import { graphql, useStaticQuery } from 'gatsby';
import { getLocationId } from '../../redux/selectors';
import useLocationData from '../../hooks/useLocationData';

const query = graphql`
    query WellnessPromoSettingsQuery {
        settings {
            wellnessPromo {
                link
                copy
                mobileLink
                start
                end
                overridePromoDays
                debug
            }
        }
    }
`;

type WellnessPromoSettingsQuery = {
    settings: Maybe<{
        wellnessPromo: Maybe<ReadonlyArray<WellnessPromoSettings>>;
    }>;
};

type WellnessWednesdayState = {
    start: Date;
    end: Date;
    showInfo: boolean;
    timeZone: string;
};

type WellnessWednesdayResult = Omit<WellnessWednesdayState, 'timeZone'> & {
    promoDays: ReadonlyArray<string>;
    daysOfWeek: ReadonlyArray<string>;
    copy: string;
    link: string;
    hasMobileLink?: boolean;
};

type WellnessWednesdayProps = {
    week: Moment;
    month: Moment;
    customLocationPromoDays?: ReadonlyArray<string>;
};

const DEFAULT_PROMO_DAYS = ['wednesday'];

export default function useWellnessWednesday({
    week: weekMoment,
    month: monthMoment,
    customLocationPromoDays,
}: WellnessWednesdayProps): WellnessWednesdayResult | null {
    const { settings } = useStaticQuery<WellnessPromoSettingsQuery>(query);

    const promo = React.useMemo(() => {
        const today = new Date();
        return settings?.wellnessPromo?.find(
            ({ start, end, debug }: WellnessPromoSettings) =>
                debug ||
                isWithinInterval(today, {
                    start: parse(start, 'yyyy-MM-dd', new Date()),
                    end: parse(end, 'yyyy-MM-dd', new Date()),
                }),
        );
    }, [settings]);

    const startString = promo?.start;
    const endString = promo?.end;
    const overridePromoDays = promo?.overridePromoDays
        ?.split(',')
        .map((day: string) => day.toLowerCase().trim());
    const debug = !!promo?.debug;
    const link = promo?.link;
    const copy = promo?.copy;
    const hasMobileLink = promo?.mobileLink;

    const isMobile = useIsMobile();
    const { week, month } = React.useMemo(
        () => ({
            week: weekMoment.toDate(),
            month: monthMoment.toDate(),
        }),
        [weekMoment, monthMoment],
    );
    const [state, setState] = React.useState<WellnessWednesdayState | null>(
        null,
    );
    const locationId = useSelector(getLocationId);
    const location = useLocationData(locationId);

    React.useEffect(() => {
        if (startString && endString) {
            const timeZone = location?.timezone ?? FALLBACK_TIMEZONE;
            const start = toZonedTime(`${startString}T00:00:00Z`, timeZone);
            const end = toZonedTime(`${endString}T23:59:59Z`, timeZone);

            setState({
                start,
                end,
                timeZone,
                showInfo:
                    debug ||
                    isWithinInterval(new Date(), {
                        start,
                        end,
                    }),
            });
        }
    }, [endString, startString, location?.timezone, debug]);

    return React.useMemo(() => {
        if (!state || !state.showInfo) {
            return null;
        }

        const { start, end, timeZone } = state;

        const granularity = isMobile ? 'week' : 'month';
        const startOfFunction =
            granularity === 'week' ? startOfWeek : startOfMonth;
        const endOfFunction = granularity === 'week' ? endOfWeek : endOfMonth;
        const reference = isMobile ? week : month;
        let locationPromoDays: readonly string[] =
            overridePromoDays ?? DEFAULT_PROMO_DAYS;

        if (
            !overridePromoDays?.length &&
            customLocationPromoDays &&
            customLocationPromoDays.length > 0
        ) {
            locationPromoDays = customLocationPromoDays;
        }

        if (
            isWithinInterval(reference, {
                start: startOfFunction(start),
                end: endOfFunction(end),
            })
        ) {
            const initialDate = toDate(reference, { timeZone });
            const isSameFunction =
                granularity === 'week' ? isSameWeek : isSameMonth;

            let date = startOfFunction(initialDate);

            const promoDays: string[] = [];
            while (isSameFunction(reference, date)) {
                if (
                    isWithinInterval(date, { start, end }) &&
                    locationPromoDays.includes(
                        format(date, 'EEEE').toLowerCase(),
                    )
                ) {
                    promoDays.push(format(date, 'yyyy-MM-dd'));
                }
                date = addDays(date, 1);
            }
            const daysOfWeek = locationPromoDays.map((dayOfWeek) =>
                format(parse(dayOfWeek, 'EEEE', new Date()), 'EEEE'),
            );

            return {
                start,
                end,
                showInfo: state.showInfo,
                promoDays,
                daysOfWeek,
                copy,
                link,
                hasMobileLink,
            };
        }

        return null;
    }, [
        state,
        isMobile,
        week,
        month,
        overridePromoDays,
        customLocationPromoDays,
        link,
        copy,
        hasMobileLink,
    ]);
}
